import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { useToast } from "../../hooks/use-toast";
import { cn } from "../../lib/utils";
import { Plus, Loader2, Siren, Search } from "lucide-react";

const SEVERITY_CLS = {
  High: "border-red-500/40 bg-red-500/10 text-red-600",
  Medium: "border-amber-500/40 bg-amber-500/10 text-amber-600",
  Low: "border-sky-500/40 bg-sky-500/10 text-sky-600",
};

const STATUS_CLS = {
  open: "border-red-500/40 bg-red-500/10 text-red-600",
  acknowledged: "border-amber-500/40 bg-amber-500/10 text-amber-600",
  resolved: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600",
  closed: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600",
  RESOLVED: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600",
  CLOSED: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600",
};

const STATUS_OPTIONS = ["open", "acknowledged", "resolved", "closed"];

function LogIncidentModal({ open, onClose, onSaved }) {
  const { toast } = useToast();
  const [title, setTitle] = useState("");
  const [severity, setSeverity] = useState("Low");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!title.trim()) { toast({ title: "Title is required", variant: "destructive" }); return; }
    setBusy(true);
    try {
      await api.post("/compliance/incidents", { title, severity, description: description || null, source: "StaffReported" });
      toast({ title: "Incident logged" });
      setTitle(""); setSeverity("Low"); setDescription("");
      onSaved?.();
    } catch (e) {
      toast({ title: "Couldn't log incident", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="compliance-incident-modal">
        <DialogHeader>
          <DialogTitle>Log incident</DialogTitle>
          <DialogDescription>Record a security or operational incident.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" autoFocus data-testid="compliance-incident-title-input" />
          </div>
          <div>
            <Label>Severity</Label>
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger className="mt-1" data-testid="compliance-incident-severity-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Low", "Medium", "High"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1" data-testid="compliance-incident-description-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="compliance-incident-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={busy} className="gap-1.5" data-testid="compliance-incident-save-btn">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {busy ? "Logging…" : "Log incident"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ComplianceIncidents({ isAdmin }) {
  const { toast } = useToast();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [search, setSearch] = useState("");
  const [severityFilter, setSeverityFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/compliance/incidents");
      setIncidents(data.incidents || []);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (incident, status) => {
    setUpdatingId(incident.id);
    try {
      await api.put(`/compliance/incidents/${incident.id}`, { status });
      setIncidents((prev) => prev.map((i) => (i.id === incident.id ? { ...i, status } : i)));
    } catch (e) {
      toast({ title: "Couldn't update incident", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setUpdatingId(null); }
  };

  if (loading) return <div className="h-64 rounded-xl bg-muted/40 animate-pulse" />;

  const openCount = incidents.filter((i) => !["resolved", "closed", "RESOLVED", "CLOSED"].includes(i.status)).length;
  const filtered = incidents.filter((i) => {
    if (severityFilter !== "all" && i.severity !== severityFilter) return false;
    if (search && !`${i.title} ${i.source}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-3" data-testid="compliance-incidents-page">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search incidents…" className="pl-8 h-9" data-testid="compliance-incidents-search" />
        </div>
        <Select value={severityFilter} onValueChange={setSeverityFilter}>
          <SelectTrigger className="h-9 w-[150px]" data-testid="compliance-incidents-severity-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All severities</SelectItem>
            <SelectItem value="High">High</SelectItem>
            <SelectItem value="Medium">Medium</SelectItem>
            <SelectItem value="Low">Low</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-[12.5px] text-muted-foreground ml-1">{openCount} open / {incidents.length} total</div>
        {isAdmin && (
          <Button size="sm" className="gap-1.5 ml-auto" onClick={() => setModalOpen(true)} data-testid="compliance-incident-add-btn">
            <Plus className="w-3.5 h-3.5" /> Log incident
          </Button>
        )}
      </div>
      <div className="rounded-xl border border-border bg-card divide-y divide-border">
        {filtered.map((inc) => (
          <div key={inc.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`compliance-incident-row-${inc.id}`}>
            <div className="flex items-center gap-2.5 min-w-0">
              <Siren className="w-4 h-4 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <div className="text-[12.5px] font-medium truncate">{inc.title}</div>
                <div className="text-[11px] text-muted-foreground">
                  {inc.source}
                  {inc.occurred_at ? ` · ${new Date(inc.occurred_at).toLocaleDateString()}` : ""}
                  {inc.closed_at ? ` · closed ${new Date(inc.closed_at).toLocaleDateString()}` : ""}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className={cn("text-[10px] font-semibold uppercase rounded-full border px-2 py-0.5", SEVERITY_CLS[inc.severity] || SEVERITY_CLS.Low)}>{inc.severity}</span>
              {isAdmin ? (
                <Select value={inc.status} onValueChange={(v) => setStatus(inc, v)} disabled={updatingId === inc.id}>
                  <SelectTrigger className="h-7 w-[120px] text-[11px]" data-testid={`compliance-incident-status-select-${inc.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <span className={cn("text-[10px] font-semibold uppercase rounded-full border px-2 py-0.5", STATUS_CLS[inc.status] || STATUS_CLS.open)}>{inc.status}</span>
              )}
            </div>
          </div>
        ))}
        {incidents.length === 0 && <div className="text-[11.5px] text-muted-foreground text-center py-8">No incidents logged</div>}
        {incidents.length > 0 && filtered.length === 0 && <div className="text-[11.5px] text-muted-foreground text-center py-8" data-testid="compliance-incidents-empty">No incidents match.</div>}
      </div>
      <LogIncidentModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={() => { setModalOpen(false); load(); }} />
    </div>
  );
}
